import {
  AfterViewChecked,
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding, HostListener,
  Input, NgZone,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
  ViewContainerRef,
} from '@angular/core';

import { SohoBusyIndicatorDirective, SohoTabsComponent, } from 'ids-enterprise-ng';
import { LandmarkPanel, LMFormContentBase } from './lm-form.model';
import { LMFormPanelBase } from './lm-form-panel-base';
import { LMFormPanelBaseService, LMPanelTransactionEvent } from '@form/services/lm-form-panel.service';
import { LMModel, LMModelChangedEvent } from './model/lm-model';
import { LMFormState } from '@states/models/lm-form-state.model';
import { LMFormPersonalization } from '@states/models/lm-form-personalization.model';
import { LMBusyIndicatorService } from '@services/lm-busyindicator.service';
import { LMWorker } from '../../types/lm-worker';
import { LMInvalidFieldEvent } from '@field/lm-field-base.component';
import { LMContextService, LMLoggerService, LMUrlUtilService } from '@services/index';
import { LMFormPanelFormComponent } from './lm-form-panel-form.component';
import { LMDashboardViewComponent } from '@dashboard/lm-dashboard-view.component';
import { Observable, Subject, Subscription } from 'rxjs';
import { LMErrorUtilService } from '@services/utils/lm-error-util.service';
import { AppConstants, LMBrowserUtilService, LMConditionUtilService } from '@utils/index';
import { takeUntil } from 'rxjs/operators';
import { LMFormPanelMultiListComponent } from './lm-form-panel-multilist.component';
import { LMColorId, LMThemeService } from '@banner/lm-theme.service';
import { DomSanitizer, SafeStyle } from '@angular/platform-browser';
import { LMModelField } from './model';
import { LMFormPanelUrlComponent } from './lm-form-panel-url.component';
import { LMFormPanelDashboardComponent } from './lm-form-panel-dashboard.component';
import { LMListTransactionEvent } from '@list/lm-list.component';
import { LMFormService, LMFormSpecUtilService } from './services';
import { LMGlobalPersonalizationService } from '@states/services';
import { LMActionFormDialogResultCode } from '@modal/index';
import { LMFormPanelListComponent, LMListSpecUtilService, LMRefreshRequest } from '@app/components';

export class LMPanelSaveWorker extends LMWorker {
  private readonly formPanels: LMFormPanelBase[];

  constructor(formPanels: LMFormPanelBase[]) {
    super();

    this.formPanels = formPanels;
  }

  protected has(i: number): boolean {
    return (i < this.formPanels.length);
  }

  protected get(i: number): LMFormPanelBase {
    return this.formPanels[ i ];
  }

  protected run(i: number): Observable<boolean> {
    return new Observable<boolean>(observer => {
      const formPanel = this.formPanels[ i ];
      if (formPanel?.isDirty()) {
        formPanel.save(observer);
      } else {
        observer.next(true);
        observer.complete();
      }
    });
  }
}

@Component({
  selector:    'lm-form-content-composite',
  providers:   [ LMFormPanelBaseService ],
  templateUrl: 'lm-form-content-composite.component.html',
  styleUrls:   [ 'lm-form-content-composite.component.scss' ]
})
export class LMFormContentCompositeComponent implements AfterViewInit, AfterViewChecked, OnInit, OnDestroy, LMFormContentBase {

  @HostBinding('class.on-side') showContextFormOnSide = true;
  @HostBinding('class.implements-cart-view') get implementsCart() { return this.formSpec.busFormSpec.implementsCartView; }
  @HostBinding('class.lm-is-compact-form-layout') isCompactFormLayout: boolean;
  @HostBinding('class.lm-vertical-tabs') verticalTabs: boolean;
  @HostBinding('class.lm-parent-is-modal') @Input() parentIsModal: boolean;

  // Need to know if the window has shrunk, in order to correctly set the tab container height
  @HostListener('window:resize') public onResize() {
    this.doRecalculateHeight = true;
  }

  @Input() formOptions: LMFormOptions;
  @Input() specFields: LMSpecFields;
  @Input() formUrlInfo: LMFormUrlInfo = null;
  @Input() formSpec: LMFormSpec = null;
  @Input() model: LMModel;
  @Input() formState: LMFormState;
  @Input() placeHolder: ViewContainerRef;
  @Input() formPersonalization: LMFormPersonalization;

  @ViewChildren('compositeComponent') compositeComponents: QueryList<LMFormPanelBase>;
  @ViewChildren('flexContent') flexContent: QueryList<ElementRef>;
  @ViewChild('contextContext') contextContent: ElementRef;
  @ViewChild('dashboardViewComponent') dashboardViewComponent: LMDashboardViewComponent;
  @ViewChild('tabPanelContainer', { read: ElementRef }) tabContainerElement: ElementRef;
  @ViewChild('sohoTabsComponent') sohoTabsComponent: SohoTabsComponent;
  @ViewChild(SohoBusyIndicatorDirective, { static: true }) sohoBusyIndicator: SohoBusyIndicatorDirective;

  @Output() contentModified = new EventEmitter<any>();
  @Output() fieldEvent: EventEmitter<LMFieldEvent> = new EventEmitter<LMFieldEvent>();
  @Output() dashboardContentDisplayed = new EventEmitter<LMContentDisplayedEvent>();
  @Output() updateToolbarStateEvent = new EventEmitter<any>();
  @Output() sendContextMessageEvent = new EventEmitter<any>();
  @Output() specialActionsToolbarButtonEvent = new EventEmitter<LMSpecialActionToolbarButtonEvent>();
  @Output() panelTransactionEvent = new EventEmitter<LMPanelTransactionEvent>();

  public panels: Array<LandmarkPanel>;
  public fromDataView: LMDataView;
  public contextForm: LMContextForm;
  public useThemeColor: boolean;
  public colorIsDefault: boolean;
  public useContextFormBackgroundImage: boolean;
  public personalize: boolean;
  public contextFormBackgroundImageUrl: SafeStyle;
  public formPanelUrlDisplayed = false;
  public activatedTabChanged: boolean;
  public useLazyLoadForm = false;
  public onSubForm = false;

  private _hasPendingSelection = false;
  private _pendingSelectedPanel: LandmarkPanel;
  private m_componentDestroyed = false;
  private subscriptionManager: Subject<void> =  new Subject<void>();
  private instanceId: string;

  private runHandleResizeTabsComponent = false;
  private initializeSohoTabs = false;
  private selectedPanelIndex: number;
  public panelVisibilityUpdateTimeStamp: number;

  private _singlePanel: LandmarkPanel;
  private _hasTabs: boolean;

  private doRecalculateHeight = true;
  public tabPanelContainerHeight = 'auto';
  public hideBorder = false;
  public testVersion1: boolean;
  public cartHeight: '700px;'

  // eslint-disable-next-line @typescript-eslint/ban-types
  public formPanelTabAttributes: SohoAttributesArray = [];

  public readonly tabIdSuffix = AppConstants.ELEMENT_ID_SUFFIX_FORM_PANEL_TAB;
  public uiLoadingIndicatorTimeout = 0;

  private visibleWhenEventSubscription: Subscription;

  public use100HeightContainer = false;

  public useFullViewPointBackground = false;
  public fullViewPointBackgroundImageUrl: SafeStyle;
  public foregroundLight = false;
  public foregroundDark = false;
  public fullPageBackgroundNotTwelveColumn = false;

  constructor(
    private lmBusyIndicatorService: LMBusyIndicatorService,
    private lmFormPanelBaseService: LMFormPanelBaseService,
    private lmLoggerService: LMLoggerService,
    private lmErrorUtilService: LMErrorUtilService,
    private lmConditionUtilService: LMConditionUtilService,
    private ngZone: NgZone,
    private lmBrowserUtil: LMBrowserUtilService,
    private lmThemeService: LMThemeService,
    private lmUrlUtilService: LMUrlUtilService,
    private _sanitizer: DomSanitizer,
    private lmContextService: LMContextService,
    private lmFormService: LMFormService,
    private lmFormSpecUtilService: LMFormSpecUtilService,
    private lmGlobalPersonalizationService: LMGlobalPersonalizationService,
    private lmListSpecUtilService: LMListSpecUtilService
  ) {
    this.instanceId = this.lmLoggerService.instanceId;
    this.useLazyLoadForm = this.lmContextService.useLazyLoadForm();
    this.uiLoadingIndicatorTimeout = this.lmContextService.uiLoadingIndicatorTimeout;
  }

  ngOnInit() {
    this.lmLoggerService.log([ this, this.instanceId, 'ngOnInit()' ]);

    this.testVersion1 = this.lmContextService.testVersion() === AppConstants.TESTVERSION_V1;

    this.formPanelTabAttributes.push({ name: 'data-automation-id', value: 'lm-' + this.formSpec.formElementId + '-tab' });

    if (!this.testVersion1) {
      this.formPanelTabAttributes.push({ name: 'id', value: this.formSpec.formElementId + '_tab' });
    }

    this.lmFormPanelBaseService.buildCompositePanels(this.formSpec.busFormSpec, this.model, this.formSpec, true);
    this.panels = this.lmFormPanelBaseService.getPanels();
    this.isCompactFormLayout = this.lmGlobalPersonalizationService.getGlobalPersonalization().useCompactFormLayout;

    if (this.formSpec.busFormSpec.background) {
      this.useFullViewPointBackground = this.formSpec.busFormSpec.background.useBackgroundImage;
    }

    if (this.formSpec.busFormSpec.contextForm && this.formSpec.busFormSpec.implementsDashBoardView) {
      this.showContextFormOnSide = !this.formSpec.busFormSpec.showContextFormOnTop;
    } else if (this.formSpec.busFormSpec.contextForm && !this.formSpec.busFormSpec.implementsDashBoardView) {
      this.showContextFormOnSide = !!this.formSpec.busFormSpec.showContextFormOnLeft;
    } else {
      this.showContextFormOnSide = false;
    }

    // Force tabs to display horizontal if context form is on the side.
    this.verticalTabs = this.lmFormPanelBaseService.getVerticalTabs() && !this.showContextFormOnSide;
    this.contextForm = this.lmFormPanelBaseService.getContextForm();
    this.onSubForm = this.lmFormSpecUtilService.isSubForm(this.formSpec);

    this.selectedPanelIndex = this.formState.selectedPanelIndex;

    if (this.selectedPanelIndex === undefined || this.selectedPanelIndex >= this.panels.length || this.selectedPanelIndex < 0) {
      this.selectedPanelIndex = 0;
    } else {
      const selectedPanel = this.panels[ this.selectedPanelIndex ];
      if (selectedPanel && selectedPanel.hidden) {
        for (let i = 0, length = this.panels.length; i < length; i++) {
          if (!this.panels[ i ].hidden) {
            this.panels[ i ].selected = true;
            this.selectedPanelIndex = i;
            break;
          }
        }
      } else {
        if (selectedPanel) {
          selectedPanel.selected = true;
        }
      }
    }

    if (!this.verticalTabs && !this.showContextFormOnSide) {
      // ---------------------------------------------------------------
      // only apply context form styling if tabs are NOT vertical.
      // ---------------------------------------------------------------
      if (!this.contextForm) {
        // ---------------------------------------------------------------
        // if no context form then use personalize color for tabs.
        // ---------------------------------------------------------------
        this.useThemeColor = true;
        this.colorIsDefault = this.lmThemeService.getCurrentColorId() === LMColorId.default;

      } else {
        this.personalize = this.lmThemeService.isPersonalized(true, this.formSpec.busFormSpec, this.parentIsModal);

        if (this.personalize && this.contextForm.background) {
          if (this.contextForm.background.useThemeColor) {
            this.useThemeColor = true;
            this.colorIsDefault = this.lmThemeService.getCurrentColorId() === LMColorId.default;

            // // todo should have been handled automatically for "default" theme color. Ping tim
            // this.lmThemeService.getColorChangeSubject().subscribe(colorId => {
            //   // default color means no color is used for menu, context forms, and composite tabs.
            //   this.colorIsDefault = colorId === LMColorId.default
            // });

          } else if (this.contextForm.background.useBackgroundImage) {
            this.useContextFormBackgroundImage = true;
          }
        }
      }
    }

    this.panels.forEach((panel) => {
      if (panel.type === 'dashboard') {
        panel.dashBoardPanes = this.lmFormPanelBaseService.createSubPanels(panel.busFormPanel.panels, this.formSpec.busFormSpec, this.model, this.formSpec);
        if (panel.busFormPanel.dashboardColumns) {
          panel.dashboardColumns = panel.busFormPanel.dashboardColumns;
        }
      }
    });

    this._calculateHasTabs();
    this._setSinglePanel();
    this._setBackgroundImageUrl();

    this.model.modelChanged.pipe(takeUntil(this.subscriptionManager)).subscribe((modelChangedEvent: LMModelChangedEvent) => {
      let sourceIsPanel = false;
      let originatingPanel: string = null;
      let originatingList: string = null;

      if (modelChangedEvent.source) {
        if (modelChangedEvent.source instanceof LMPanelTransactionEvent) {
          sourceIsPanel = true;
          originatingPanel = modelChangedEvent.source.originatingPanel;
          originatingList = modelChangedEvent.source.originatingList;
        } else if (modelChangedEvent.source instanceof LMListTransactionEvent) {
          sourceIsPanel = true;
          originatingList = modelChangedEvent.source.eventSource ? modelChangedEvent.source.eventSource.listId : null;
        }
      }

      this.updateVisibleWhenTabs();

      if (modelChangedEvent.eventType !== 'update') {
        this.updateFromDataView(sourceIsPanel, originatingList, originatingPanel);
      }
    });

    this.lmConditionUtilService.evaluateConditions.pipe(takeUntil(this.subscriptionManager)).subscribe(() => {
      this.updateVisibleWhenTabs();
    });

    this.initializeSohoTabs = true;

    this.visibleWhenEventSubscription = this.lmFormService.visibleWhenEvent$
      .subscribe((elementRef: ElementRef) => this.onVisibleWhenEvent(elementRef));

    const panel = this.panels[ this.selectedPanelIndex ];
    this.use100HeightContainer = panel?.type === 'formlist' && this.lmListSpecUtilService.isCardViewWithDetailSections(panel.listSpec) && !this.verticalTabs && !this.contextForm;
  }

  ngAfterViewInit() {
    this.lmLoggerService.log([ this, this.instanceId, 'ngAfterViewInit()' ]);
    this.runHandleResizeTabsComponent = true;

    // set fromDataView from models dataview so if flows down to the
    // related composite form panels (multi-list, list, etc..).
    setTimeout(() => this.fromDataView = this.model.getData());
  }

  ngAfterViewChecked() {
    if (this.runHandleResizeTabsComponent) {
      this.runHandleResizeTabsComponent = false;
      if (this.hasTabs && this.sohoTabsComponent) {
        this.sohoTabsComponent.handleResize();
      }
    }

    if (this.initializeSohoTabs) {
      this.initializeSohoTabs = false;
      if (this.hasTabs && this.sohoTabsComponent) {
        this.sohoTabsComponent.updated();
        if (this.selectedPanelIndex > -1) {
          const nearestPanel = this.findNearestVisibleValidPanel(this.selectedPanelIndex);
          if (nearestPanel) {
            if (this.sohoTabsComponent) {
              this.sohoTabsComponent.select('#' + nearestPanel.id);
            }
          }
        }
      }
    }

    if (this._hasPendingSelection) {
      setTimeout(() => this.handlePendingSelection());
      this._hasPendingSelection = false;
    }

    if (this.doRecalculateHeight) {
      this.doRecalculateHeight = false;


    }
  }

  ngOnDestroy() {
    this.setComponentDestroyed();

    this.subscriptionManager.next();
    this.subscriptionManager.complete();

    this.visibleWhenEventSubscription.unsubscribe();
  }

  onContentModified(event: any) {
    this.contentModified.emit(event);
  }

  onFieldEvent(event) {
    if ('FieldInvalidEvent' === event.eventType) {
      // switch to the tab with the field in error
      const fieldChangedEvent = event as LMInvalidFieldEvent;

      if (fieldChangedEvent.panelId) {
        // find the visible tab and select it
        if (this.panels.some( (panel: LandmarkPanel) => panel.id === fieldChangedEvent.panelId && !panel.hidden)) {
          this.sohoTabsComponent.select('#' + fieldChangedEvent.panelId);

        } else {
          // cannot find a panel, so display the error in a dialog
          this.lmErrorUtilService.handleUnexpectedError({ title: Soho.Locale.translate('Error'), message: event.alert.errorMessage});
        }
      }

      fieldChangedEvent.alert.scrollIntoView(false);
    }
    this.fieldEvent.emit(event);
  }

  onUpdateToolbarStateEvent() {
    this.updateToolbarStateEvent.emit();
  }

  onSendContextMessageEvent(event: any) {
    this.sendContextMessageEvent.emit(event);
  }

  onSpecialActionToolbarButtonEvent(event: LMSpecialActionToolbarButtonEvent) {
    this.specialActionsToolbarButtonEvent.emit(event);
  }

  onPanelTransactionEvent(event: LMPanelTransactionEvent) {
    this.panelTransactionEvent.emit(event);
  }

  onTabActivated(event: SohoTabsEvent) {
    /*
     * onTabActivated gets messaged before the sohoDataGrid has finished resolving it's template
     * so I need to put into a setTimeout allowing angular to have a chance to process the template
     * before I attempt to call methods on it.
     */
    setTimeout(() => {
      if (this.componentDestroyed) {
        this.lmLoggerService.log([ this, this.instanceId, 'onTabActivated() > timeout > component was destroyed before expected, returning.' ]);
        // safety in case the user has initiated a list but quickly navigated
        // to another screen and ngOnDestroy is in a race condition with this
        // timeout.
        return;
      }

      const selectedPanelIndex = this.activeTabIndex;
      const panel = this.panels[ selectedPanelIndex ];
      let showBusy = false;

      // same criteria as when activating a lazy load form tab
      if (panel && panel.type === 'form' && this.useLazyLoadForm && !panel.hidden && !panel.initialized) {
        // use the global busy indicator as it will run immediately
        this.lmBusyIndicatorService.showBusyIndicator();
        showBusy = true;
      }

      if (showBusy) {
        // if showBusy, then another timeout so the busy indicator can start
        setTimeout(() => {
          this.doActivateTab();
        }, 50);

      } else {
        this.doActivateTab();
      }
    }, 1);
  }

  private doActivateTab() {
    this.activateTab();
    this.doRecalculateHeight = true;
    this.activatedTabChanged = !this.activatedTabChanged;
  }

  // Need to set the height on the tab container correctly. If the content of the panel is less than the
  // window height, need to set the containers height to match the view height of the window. If the content
  // of the tab is greater than the window height, then set the tab container's height to auto
  private handlePanelHeightChange() {
    // Set possible window height modifiers
    const contextFormOffset = (this.contextForm && !this.showContextFormOnSide) ? this.contextContent.nativeElement.offsetHeight : 0;

    const mingleOffset = this.lmContextService.inMingle() ? 34 : 0;

    const headerBarOffset = !this.lmContextService.isHideTitlebar() ? 60 : 0;

    const windowHeight = window.innerHeight - mingleOffset - headerBarOffset;

    if (this.hasTabs && this.verticalTabs) {

      // Set height to 100% if in responsive mode
      if (window.innerWidth < Soho.breakpoints['phone-to-tablet']) {
        this.tabPanelContainerHeight = '100%';
        // When going into responsive view, the border still appeared on the left side. Need to hide it.
        this.hideBorder = true;
        return;
      }

      // reset hiding border
      this.hideBorder = false;

      const selectedPanel = this.panels[ this.selectedPanelIndex ];

      // rule from the template
      if (this.formPanelUrlDisplayed) {
        this.tabPanelContainerHeight = '100%';
        return;
      }

      // Multi list panels are with over 2 lists always huge. Set to auto, so we don't have a timing issue waiting
      // for the lists to get populated with data
      if (selectedPanel.type === 'multilist') {
        let visibleLists = 0;
        this.compositeComponents.filter((basePanel: LMFormPanelBase) => basePanel.panelId === selectedPanel.id)
          .forEach((basePanel: LMFormPanelBase) => visibleLists = (basePanel as LMFormPanelMultiListComponent).numberVisibleLists);

        if (visibleLists > 2) {
          this.tabPanelContainerHeight = 'auto';
          return;
        }
      }

      const thePanel = this.compositeComponents.find((panel: LMFormPanelBase) => panel.panelId === selectedPanel.id);

      const panelHeight = thePanel ? thePanel.getPanelHeight() : 0;
      this.calculateContentPanelHeight(panelHeight, contextFormOffset, windowHeight);

    } else if (this.formSpec.busFormSpec.implementsDashBoardView) {
      const panelHeight = this.dashboardViewComponent ? this.dashboardViewComponent.getPanelHeight() : 0;
      this.calculateContentPanelHeight(panelHeight, contextFormOffset, windowHeight);
    } else if (this.formSpec.busFormSpec.implementsCartView) {
      const panelHeight = this.dashboardViewComponent ? this.dashboardViewComponent.getPanelHeight() : 0;
      this.calculateContentPanelHeight(panelHeight, contextFormOffset, windowHeight);
    } else {
      // TODO this single panel case may be able to included in the first if block.
      const selectedPanel = this.panels[ this.selectedPanelIndex ];
      const thePanel = this.compositeComponents.find((panel: LMFormPanelBase) => panel.panelId === selectedPanel.id);
      const panelHeight = thePanel ? thePanel.getPanelHeight() : 0;
      this.calculateContentPanelHeight(panelHeight, contextFormOffset, windowHeight);

    }
  }

  private calculateContentPanelHeight(panelHeight: number, contextFormOffset: number, windowHeight: number): void {
    let isNestedCardViewWithDetailsWithoutContext = false;
    const tabHeaderHeight = 40; //TODO: compute exact height

    // Determine if selected panel is a list panel with a card view with details sections in it, and the composite form
    // has a context form
    if (this.selectedPanelIndex > -1 && this.panels[this.selectedPanelIndex].type === 'formlist' && this.formSpec.busFormSpec.contextForm) {
      const listSpec = this.panels[this.selectedPanelIndex].listSpec;
      isNestedCardViewWithDetailsWithoutContext = this.lmListSpecUtilService.isCardViewWithDetailSections(listSpec);
    }

    if ((panelHeight + contextFormOffset) < windowHeight && !this.parentIsModal && !isNestedCardViewWithDetailsWithoutContext) {

      if (!this.contextForm || this.showContextFormOnSide) {
        this.tabPanelContainerHeight = this.hasTabs && !this.verticalTabs ? `calc(100% - ${tabHeaderHeight}px)` : '100%';
      } else {
        const heightOffset = tabHeaderHeight + contextFormOffset;
        this.tabPanelContainerHeight = 'calc(100% - ' + heightOffset + 'px)';
      }

    } else if (isNestedCardViewWithDetailsWithoutContext && !this.showContextFormOnSide) {
      // Context form NOT on left, formlist with detail section
      this.tabPanelContainerHeight = '100%';
    } else if(!this.contextForm || this.formSpec.busFormSpec.showContextFormOnLeft){
      this.tabPanelContainerHeight = this.hasTabs && !this.verticalTabs ? `calc(100% - ${tabHeaderHeight}px)` : '100%';
    } else {
      this.tabPanelContainerHeight = 'auto';
    }
  }

  get isHideTitleBar(): boolean {
    return this.lmContextService.isHideTitlebar() || this.lmContextService.inMingleInbox();
  }

  get singlePanel(): LandmarkPanel {
    return this._singlePanel;
  }

  private _setSinglePanel(): void {
    if (this.formSpec.busFormSpec.implementsDashBoardView) {
      this._singlePanel = null;
    } else {
      // only filter out hidden panels. Non-hidden panels that are invalid should
      // be displayed as disabled.
      const panels = this.panels.filter((panel: LandmarkPanel) => !panel.hidden &&  panel.isValid);
      this._singlePanel = panels.length === 1 ? panels[0] : null;
    }
  }

  private _setBackgroundImageUrl(): void {
    if (this.useContextFormBackgroundImage) {

      const relatedImage = !!this.formSpec.busFormSpec.contextForm.background.imageFieldId;
      const imageString = relatedImage ? this.formSpec.busFormSpec.contextForm.background.imageFieldId : this.formSpec.busFormSpec.contextForm.background.image;

      this.contextFormBackgroundImageUrl = this._buildBackgroundImageUrl(relatedImage, imageString);
    }

    if (this.useFullViewPointBackground) {
      const relatedImage = !!this.formSpec.busFormSpec.background.imageFieldId;
      const imageString = relatedImage ? this.formSpec.busFormSpec.background.imageFieldId : this.formSpec.busFormSpec.background.image;

      this.fullViewPointBackgroundImageUrl = this._buildBackgroundImageUrl(relatedImage, imageString);

      this.foregroundDark = this.formSpec.busFormSpec.background.foregroundText === 'dark';
      this.foregroundLight = this.formSpec.busFormSpec.background.foregroundText === 'light';

      // full page background only works with one panel that is a dashboard panel
      this.fullPageBackgroundNotTwelveColumn = !this.panels[0].useTwelveColumnGrid;
    }
  }

  private _buildBackgroundImageUrl(relatedValueImage: boolean, image: string): SafeStyle {
    if (relatedValueImage) {
      const imageField: LMModelField = this.model.getField(image);
      if (!imageField) {
        return;
      }

      const imageUrl: string = imageField.value;
      if (!imageUrl) {
        return;
      }

      const imageUrl2: string = this.lmUrlUtilService.removeParam(imageUrl, 'size');
      return this._sanitizer.bypassSecurityTrustStyle(`url('${imageUrl2}')`);
    } else {
      return this._sanitizer.bypassSecurityTrustStyle(`url('${image}')`);
    }
  }

  get hasTabs(): boolean {
    return this._hasTabs;
  }

  private _calculateHasTabs(): void {
    if (this.formSpec.busFormSpec.implementsDashBoardView) {
      this._hasTabs = false;
    } else {
      const visibleAndValidPanels = this.panels.filter((panel: LandmarkPanel) => !panel.hidden && panel.isValid);

      if (visibleAndValidPanels.length === 0) {
        // no tabs because we don't have any tab that can be used
        this._hasTabs = false;
      } else  {
        // We need to check if we have more than one visible panel
        // the invalid panels are displayed disabled
        const visiblePanels = this.panels.filter((panel: LandmarkPanel) => !panel.hidden);
        this._hasTabs = visiblePanels.length > 1;
      }
    }
  }

  private activateTab() {
    this.selectedPanelIndex = this.activeTabIndex;
    this.formState.selectedPanelIndex = this.selectedPanelIndex;
    const panel = this.panels[ this.selectedPanelIndex ];
    const compositeComponent: LMFormPanelBase = this.compositeComponents.toArray()[ this.selectedPanelIndex ];

    if (!compositeComponent) {
      return; // safety check!!!!!
    }

    if (panel && panel.type === 'form' && this.useLazyLoadForm && !panel.hidden && !panel.initialized) {
      // render the panel
      panel.initialized = true;
    }

    // set tab container height to 100% when the panel is a card view list with detail sections
    this.use100HeightContainer = panel?.type === 'formlist' && this.lmListSpecUtilService.isCardViewWithDetailSections(panel.listSpec) && !this.verticalTabs && !this.contextForm;

    if (!this.isDependentFormPanel(compositeComponent)) {
      return;
    }

    if (this.isFormPanelUrlPanel(compositeComponent)) {
      this.formPanelUrlDisplayed = true;
    }

    // Panel has been touch check if it needs to refresh or reload
    if (compositeComponent.activated) {
      switch (compositeComponent.panelDataState) {
        case 'stale_needsRefresh':
          // fromDataView has not been changed, so make sure the composite component has the most up to date data view
          // when activating
          compositeComponent.fromDataView = this.model.getData();
          compositeComponent.panelDataState = 'fresh';
          compositeComponent.refresh().subscribe();
          break;
        case 'stale_needsReload':
          // this.fromDataView has been changed, so just need to mark as fresh
          compositeComponent.panelDataState = 'fresh'; // fromDataView input flow to the content forcing a reload!!!
          break;
        default:
          if (compositeComponent instanceof LMFormPanelDashboardComponent) {
            // force panels to relayout in case a resize happens after this panel has been activated
            // but the user resized the browser when on another tab.
            (compositeComponent as LMFormPanelDashboardComponent).refreshPanels();
          }
      }
      return;
    } else {
      // added for LMCLIENT-32197. This was a case where on a composite panel, there was a data change from one of the
      // panels. Then there is a second panel, which has not been activated, and has visible when rules on it. Since
      // it hasn't been updated previously, it had a stale fromDataView on it and the visible when rules were incorrect.
      // So we are going to set the fromDataView for the panel here to the up to date dataview
      compositeComponent.fromDataView = this.model.getData();
    }

    // --------------------------------------------------------------------------------------
    // Include the busy indicator for form panels too as it takes
    // time to render a form panel.
    // --------------------------------------------------------------------------------------
    this.lmBusyIndicatorService.showBusyIndicator(this.sohoBusyIndicator);

    setTimeout(() => {
      if (this.componentDestroyed) {
        this.lmLoggerService.log([ this, this.instanceId, 'activateTab() > timeout > component was destroyed before expected, returning.' ]);
        // safety in case the user has initiated a list but quickly navigated
        // to another screen and ngOnDestroy is in a race condition with this
        // timeout.
        return;
      }

      this.activateFormPanel(compositeComponent);
      this.lmBusyIndicatorService.hideBusyIndicator(this.sohoBusyIndicator);
    });

  }

  private activateFormPanel(formPanel: LMFormPanelBase) {
    formPanel.panelDataState = 'fresh';
    formPanel.activated = true;  // this should trigger the template to run
  }

  onFormPanelContentLoaded(event: any) {
    // Need to set this in a timeout, else we get an expression changed error on load
    this.ngZone.runOutsideAngular(() => setTimeout(() => this.handlePanelHeightChange()));
    this.lmBusyIndicatorService.hideBusyIndicator(this.sohoBusyIndicator);
  }

  private get activeTabIndex() {
    const index = (this.sohoTabsComponent?.getActiveTab()?.length > 0)
      ? this.sohoTabsComponent.getActiveTab()[ 0 ].dataset.index
      : -1;
    return Number(index);
  }

  private updateFromDataView(sourceIsPanel?: boolean, originatingList?: string, originatingPanel?: string): void {
    this.lmLoggerService.log([this, this.instanceId, 'updateFromDataView()', 'start']);
    const newFromDataView = this.model.getData();
    const fromDataViewChanged = this.hasFromDataViewChanged(newFromDataView);

    if (!this.formSpec.busFormSpec.implementsDashBoardView) {
      this.markTabsForReload(fromDataViewChanged, sourceIsPanel, originatingList, originatingPanel);
    } else if (this.dashboardViewComponent && !fromDataViewChanged) {
      const  refreshRequest: LMRefreshRequest = {originatingSourceId: originatingList};
      this.dashboardViewComponent.refresh(refreshRequest).subscribe();
    }

    if (fromDataViewChanged) {
      this.fromDataView = newFromDataView;
    }

    this.lmLoggerService.log([this, this.instanceId, 'updateFromDataView()', 'end']);
  }

  private hasFromDataViewChanged(newFromDataView: LMDataView): boolean {
    const resourceId = this.fromDataView ? this.fromDataView.resourceId : '';
    const newResourceId = newFromDataView ? newFromDataView.resourceId : '';

    return resourceId !== newResourceId;
  }

  private isDependentFormPanel(formPanelBase: LMFormPanelBase) {
    if (!formPanelBase) {
      return false;
    }

    if (formPanelBase instanceof LMFormPanelFormComponent && !formPanelBase.formSpec.dependentForm) {
      return false;
    }

    return true;
  }

  private isFormPanelUrlPanel(formPanelBase: LMFormPanelBase) {
    if (!formPanelBase) {
      return false;
    }

    return formPanelBase instanceof LMFormPanelUrlComponent;
  }

  private markTabsForReload(fromDataViewChgd: boolean, sourceIsPanel?: boolean, originatingList?: string, originatingPanel?: string) {
    const selectedIndex = this.sohoTabsComponent ? this.activeTabIndex : 0;
    const components  = this.compositeComponents.toArray();
    components.forEach((compositeComponent, i) => {
      if (this.isDependentFormPanel(compositeComponent)) {
        if (i === selectedIndex ) {
          this.updateCurrentPanelData(compositeComponent, fromDataViewChgd, sourceIsPanel, originatingList, originatingPanel);
        } else {
          if (compositeComponent.activated && compositeComponent.panelDataState !== 'stale_needsReload') {
            compositeComponent.panelDataState = fromDataViewChgd ? 'stale_needsReload' : 'stale_needsRefresh';
          }
        }
      }
    });
  }

  private updateCurrentPanelData(compositeComponent: LMFormPanelBase, fromDataViewChgd: boolean, sourceIsPanel?: boolean, originatingList?: string, originatingPanel?: string) {
    if (compositeComponent.activated) {
      this.refreshActivePanel(compositeComponent, fromDataViewChgd, sourceIsPanel, originatingList, originatingPanel);
    } else {
      this.activateFormPanel(compositeComponent);
    }
  }

  private refreshActivePanel(compositeComponent: LMFormPanelBase, fromDataViewChgd: boolean, sourceIsPanel: boolean, originatingList?: string, originatingPanel?: string) {
    // compositeComponent.needsReload = false;
    compositeComponent.panelDataState = 'fresh';
    const isFormPanelUrl = compositeComponent instanceof LMFormPanelUrlComponent;

    // url form panels are excluded here since they sould be updated if the panel is showing.
    if (fromDataViewChgd && !isFormPanelUrl) {
      // the user changed the dataView from paging operation so
      // the new fromDataView should force the panels to refresh!!!
      return;
    }

    // we need to sort out the message passed to the hand coded screens and refreshing the form.
    // if data was updated but keys did not change should be relayed to handcoded apps but also
    // if keys have change.
    // This is used to send a message to the hand coded apps that the panel changed. It was put in primarally for action forms.
    if (isFormPanelUrl) {
      const lmFormPanelUrlComponent: LMFormPanelUrlComponent = compositeComponent as LMFormPanelUrlComponent;
      // form already displayed confirmation message soneed to send one along.
      lmFormPanelUrlComponent.fireFormActionComplete(LMActionFormDialogResultCode.ACTION_FORM_DIALOG_SAVED,
        { message: '', resourceId: this.model.getData().resourceId });
      compositeComponent.refresh().subscribe();
    }
    // user ran an action on a panel!!!
    if (sourceIsPanel) {
      this.refreshFromPanelTransaction(compositeComponent, originatingList, originatingPanel);
    } else {
      compositeComponent.refresh().subscribe();
    }
  }

  private refreshFromPanelTransaction(compositeComponent: LMFormPanelBase, originatingList?: string, originatingPanel?: string) {
    // TODO: let move to this code.  The formpanel can stop the refresh!
    // const refreshRequest: LMRefreshRequest = {originatingSourceId: originatingList, originatingPanelSourceId: originatingPanel};
    // compositeComponent.refresh(refreshRequest).subscribe(); // DO NOT FORCE REFRESH
    //
    // if (compositeComponent instanceof LMFormPanelDashboardComponent) {
    //   // force panels to relayout in case a resize happens after this panel has been activated
    //   // but the user resized the browser when on another tab.
    //   (compositeComponent as LMFormPanelDashboardComponent).refreshPanels();  // why would this need to be done here.
    // }

    // if a list or other panel triggered the event they
    // should have refreshed themselves!!!
    const activePanelChanged = compositeComponent.panelId !== originatingPanel;
    const refreshRequest: LMRefreshRequest = {originatingSourceId: originatingList};
    if (activePanelChanged) {
      compositeComponent.refresh().subscribe(); // DO NOT FORCE REFRESH
    } else {
      if (compositeComponent instanceof LMFormPanelListComponent
        || compositeComponent instanceof LMFormPanelMultiListComponent
        || compositeComponent instanceof LMFormPanelDashboardComponent) {
        // need to call list refresh because the list transaction may have begun in
        // a list detail section, in that case we need to refresh the panel list
        // need to call refresh on mulit list and dashboard to refresh the other widgets in the panel
        compositeComponent.refresh(refreshRequest).subscribe();
      }

      if (compositeComponent instanceof LMFormPanelDashboardComponent) {
        // force panels to relayout in case a resize happens after this panel has been activated
        // but the user resized the browser when on another tab.
        (compositeComponent as LMFormPanelDashboardComponent).refreshPanels();  // why would this need to be done here.
      }
      // if a list or other panel triggered the event they
      // should have refreshed themselves!!!
    }
  }

  private selectSohoTab(tabId: string) {
    if (this.sohoTabsComponent) {
      this.sohoTabsComponent.select('#' + tabId);
    }
  }

  findNearestVisibleValidPanel(selectedIndex: number): LandmarkPanel {

    for (let i = selectedIndex; i > -1; i--) {
      const panel = this.panels[i];
      if (!panel.hidden && panel.isValid) {
        return panel;
      }
    }

    for (let i = selectedIndex, length = this.panels.length; i < length; i++) {
      const panel = this.panels[i];
      if (!panel.hidden && panel.isValid) {
        return panel;
      }
    }

    return null;
  }

  isModified(): boolean {
    // composite form ( context form and form panels)
    if (this.model.isDirty) {
      return true;
    }

    if (this.dashboardViewComponent) {
      return this.dashboardViewComponent.isModified();
    } else {
      const formPanels: LMFormPanelBase[] = this.compositeComponents.toArray();
      for (let i = 0, len = formPanels.length; i < len; i++) {
        const formPanel = formPanels[ i ];
        if (formPanel.isDirty()) {
          return true;
        }
      }
    }

    return false;
  }

  // called from lm-form.component handleTransactionStatus() when setting an error on a form field; if form panel is
  // set for lazy loading, need to indicate to initialize (render) the form panel before setting the error message on the field
  canActivatePanel(fieldId): boolean {
    if (!this.useLazyLoadForm) {
      return false;
    }

    const panelIds: Array<string> = this.lmFormPanelBaseService.getPanelIdsForFieldId(fieldId);

    // iterate the panelIds array to find the first panel that meets the criteria
    for (let index = 0, len = panelIds.length; index < len; index++) {
      const panelId = panelIds[ index ];
      const aryPanels = this.panels.filter( (panel) => (panel.id === panelId && !panel.hidden && panel.type === 'form' && !panel.initialized) );

      if (aryPanels.length > 0) {
        // render the panel
        aryPanels[0].initialized = true;
        return true;
      }
    }

    return false;
  }

  runRefresh() {
    // do nothing -- listening to model events!!!
  }

  runSaveAction(): Observable<boolean> {
    if (this.dashboardViewComponent && this.dashboardViewComponent.isModified()) {
      return this.dashboardViewComponent.runSaveAction();
    }

    const formPanels: LMFormPanelBase[] = this.compositeComponents.toArray();
    // todo dpb selectedFormPanel isn't used. Please review.
    const selectedFormPanel: LMFormPanelBase = formPanels[ this.selectedPanelIndex ];
    const selectedFormPanelId = selectedFormPanel ? selectedFormPanel.panelId : '';

    return new Observable<boolean>(observer => {
      this.runSavePanels(formPanels).subscribe(
        (continueOn: boolean) => {
          formPanels.forEach(savedPanel => {
            // if the form changes, it will send an event to tell the current panel
            // to update.  All other activated tabs should be mark needs reload or refresh
            if (selectedFormPanelId === savedPanel.panelId && savedPanel.activated && savedPanel.panelDataState !== 'stale_needsReload') {
              savedPanel.panelDataState = 'stale_needsRefresh';
            }
          });
          // this.markTabsForReload(false, false);
          observer.next(continueOn);
          observer.complete();
        },
        (err => {
          const errorPanel: LMFormPanelBase = err;
          if (errorPanel) {
            this.sohoTabsComponent.select('#' + errorPanel.panelId);
            observer.next(false);
            observer.complete();
          }
        }));

    });
  }

  private updateVisibleWhenTabs() {
    this.lmFormPanelBaseService.updateCompositePanels(this.formSpec.busFormSpec, this.model, false);

    if (this.formSpec.busFormSpec.implementsDashBoardView) {
      this.panelVisibilityUpdateTimeStamp = this.lmFormPanelBaseService.getPanelDisplayStateLastChanged();

      if (this.dashboardViewComponent) {
        setTimeout(() => this.dashboardViewComponent.refreshHomepage(true));
      }

      return;
    }

    const isShowingSinglePanel = !this.hasTabs && this.singlePanel;
    this._calculateHasTabs();

    if (isShowingSinglePanel && !this.hasTabs) {
      const newSinglePanel = this.findNearestVisibleValidPanel(this.selectedPanelIndex);
      const newSinglePanelId = newSinglePanel ? newSinglePanel.id : '';
      if (this.singlePanel.id !== newSinglePanelId) {
        this.singlePanel.selected = false;
        this._hasPendingSelection = true;
        this._pendingSelectedPanel = newSinglePanel;
        this._singlePanel = null;   // this is need to clear out the single panel angular template
      }
      return;
    }

    this._setSinglePanel();
    // case multi panels showing (tabs)
    const selectedPanel = this.panels[ this.selectedPanelIndex ];
    if (selectedPanel.hidden || !selectedPanel.isValid) {
      selectedPanel.selected = false;
      const nearestPanel = this.findNearestVisibleValidPanel(this.selectedPanelIndex);

      if (nearestPanel) {
        nearestPanel.selected = true;
        if (this.sohoTabsComponent) {
          setTimeout(() => this.selectSohoTab(nearestPanel.id), 1);
          this.runHandleResizeTabsComponent = true;
        }
      }
    }
  }

  private handlePendingSelection() {
    this._setSinglePanel();

    if (this._pendingSelectedPanel) {
      this._pendingSelectedPanel.selected = true;
      const pendingSelectionId = this._pendingSelectedPanel.id;
      this._pendingSelectedPanel = null;

      if (this.hasTabs) {
        setTimeout(() => this.selectSohoTab(pendingSelectionId), 1);
      }
    }
  }

  saveComponentState(): void {
    this.compositeComponents.toArray()
      .forEach(compositeComponent =>
        compositeComponent.saveCompositeComponentState());
  }

  clearComponentState(): void {
    this.compositeComponents.toArray()
      .forEach(compositeComponent =>
        compositeComponent.clearCompositeComponentState());
  }

  trackById(index: number, panel: LandmarkPanel): string {
    return panel.id;
  }

  private runSavePanels(formPanels: LMFormPanelBase[]): Observable<boolean> {

    this.lmBusyIndicatorService.showBusyIndicator();
    const saveWorker: LMPanelSaveWorker = new LMPanelSaveWorker(formPanels);
    return saveWorker.start();
  }

  public get componentDestroyed(): boolean {
    return this.m_componentDestroyed;
  }

  protected setComponentDestroyed() {
    this.m_componentDestroyed = true;
  }

  useAlternateTabs() {
    return (!this.verticalTabs && !this.useThemeColor) || this.parentIsModal;
  }

  isPersonalizable() {
    return !this.verticalTabs && this.useThemeColor && !this.parentIsModal;
  }

  onVisibleWhenEvent(elementRef: ElementRef) {
    if ((elementRef?.nativeElement?.parentElement as HTMLElement)?.closest('.lm-composite-content-area')) {
      this.doRecalculateHeight = true;
    }
  }
}
